import styled, { css } from "styled-components";
import tw from "twin.macro";

import { BoxSizingStyle } from "../BoxSizingStyle";
import { FontStyle } from "../FontStyle";

interface IStyledTypographyProps {
  $variant:
    | "Title 1"
    | "Title 2"
    | "Header 2"
    | "Header 3"
    | "Body 1"
    | "Body 2"
    | "Caption 1"
    | "Caption 2";
  $italic: boolean;
  $tabularNums: boolean;
  $letterSpacing: "tight" | "regular" | "wide";
  $lineHeight: "tight" | "regular" | "loose";
  $align: "left" | "center" | "right" | "justify";
  $decoration: "underline" | "line-through" | "none";
  $transform: "uppercase" | "lowercase" | "capitalize" | "regular";
  $overflow: "truncate" | "ellipsis" | "visible";
  $whitespace?:
    | "regular"
    | "no-wrap"
    | "pre"
    | "pre-wrap"
    | "pre-line"
    | "break-spaces";
  $wordBreak: "regular" | "smart" | "all";
  $color:
    | "primary"
    | "secondary"
    | "tertiary"
    | "quaternary"
    | "theme-primary"
    | "theme-secondary"
    | "only-white"
    | "only-black"
    | "page-paper"
    | "success"
    | "alert"
    | "warning";
  $weight?: "light" | "regular" | "medium" | "bold";
}

const colors = {
  primary: tw`tw-text-theme-900`,
  secondary: tw`tw-text-theme-700`,
  tertiary: tw`tw-text-theme-600`,
  quaternary: tw`tw-text-theme-transparent-500`,
  "theme-primary": tw`tw-text-primary`,
  "theme-secondary": tw`tw-text-secondary`,
  "page-paper": tw`tw-text-page-paper-main`,
  "only-white": tw`tw-text-only-white`,
  "only-black": tw`tw-text-only-black`,
  success: tw`tw-text-success`,
  alert: tw`tw-text-alert`,
  warning: tw`tw-text-warning`,
};

export const StyledTypography = styled.div<IStyledTypographyProps>`
  ${FontStyle}
  ${BoxSizingStyle}

  //color
  ${(props) => colors[props.$color]}

  //variant - font size
  ${(props) =>
    props.$variant === "Title 1" &&
    css`
      ${tw`tw-text-title-1`}
    `}
  ${(props) =>
    props.$variant === "Title 2" &&
    css`
      ${tw`tw-text-title-2`}
    `}
  ${(props) =>
    props.$variant === "Header 2" &&
    css`
      ${tw`tw-text-header-2`}
    `}
  ${(props) =>
    (props.$variant === "Header 3" || props.$variant === "Body 1") &&
    css`
      ${tw`tw-text-body-1`}
    `}
  ${(props) =>
    props.$variant === "Body 2" &&
    css`
      ${tw`tw-text-body-2`}
    `}
  ${(props) =>
    props.$variant === "Caption 1" &&
    css`
      ${tw`tw-text-caption-1`}
    `}
  ${(props) =>
    props.$variant === "Caption 2" &&
    css`
      ${tw`tw-text-caption-2`}
    `}

  //italic
  ${(props) =>
    props.$italic
      ? css`
          font-style: italic;
        `
      : css`
          font-style: normal;
        `}

  //tabularNums
  ${(props) =>
    props.$tabularNums
      ? css`
          font-variant-numeric: tabular-nums;
        `
      : css`
          font-variant-numeric: normal;
        `}

  //letterSpacing
  ${(props) =>
    props.$letterSpacing === "regular" &&
    css`
      ${tw`tw-tracking-regular`}
    `}
  ${(props) =>
    props.$letterSpacing === "tight" &&
    css`
      ${tw`tw-tracking-tight`}
    `}
  ${(props) =>
    props.$letterSpacing === "wide" &&
    css`
      ${tw`tw-tracking-wide`}
    `}

  //lineHeight
  ${(props) =>
    props.$lineHeight === "regular" &&
    css`
      ${tw`tw-leading-regular`}
    `}
  ${(props) =>
    props.$lineHeight === "tight" &&
    css`
      ${tw`tw-leading-tight`}
    `}
  ${(props) =>
    props.$lineHeight === "loose" &&
    css`
      ${tw`tw-leading-loose`}
    `}

  //align
  ${(props) =>
    props.$align === "left" &&
    css`
      text-align: left;
    `}
  ${(props) =>
    props.$align === "right" &&
    css`
      text-align: right;
    `}
  ${(props) =>
    props.$align === "center" &&
    css`
      text-align: center;
    `}
  ${(props) =>
    props.$align === "justify" &&
    css`
      text-align: justify;
    `}

  //decoration
  ${(props) =>
    props.$decoration === "none" &&
    css`
      text-decoration: none;
    `}
  ${(props) =>
    props.$decoration === "underline" &&
    css`
      text-decoration: underline;
    `}
  ${(props) =>
    props.$decoration === "line-through" &&
    css`
      text-decoration: line-through;
    `}

  //transform
  ${(props) =>
    props.$transform === "regular" &&
    css`
      text-transform: none;
    `}
  ${(props) =>
    props.$transform === "uppercase" &&
    css`
      text-transform: uppercase;
    `}
  ${(props) =>
    props.$transform === "lowercase" &&
    css`
      text-transform: lowercase;
    `}
  ${(props) =>
    props.$transform === "capitalize" &&
    css`
      text-transform: capitalize;
    `}

  //overflow
  ${(props) =>
    props.$overflow === "visible" &&
    css`
      overflow: visible;
    `}
  ${(props) =>
    props.$overflow === "ellipsis" &&
    css`
      text-overflow: ellipsis;
    `}
  ${(props) =>
    props.$overflow === "truncate" &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}

  //whitespace
  ${(props) =>
    props.$whitespace === "regular" &&
    css`
      white-space: normal;
    `}
  ${(props) =>
    props.$whitespace === "no-wrap" &&
    css`
      white-space: nowrap;
    `}
  ${(props) =>
    props.$whitespace === "pre" &&
    css`
      white-space: pre;
    `}
  ${(props) =>
    props.$whitespace === "pre-wrap" &&
    css`
      white-space: pre-wrap;
    `}
  ${(props) =>
    props.$whitespace === "pre-line" &&
    css`
      white-space: pre-line;
    `}
  ${(props) =>
    props.$whitespace === "break-spaces" &&
    css`
      white-space: break-spaces;
    `}

  //wordBreak
  ${(props) =>
    props.$wordBreak === "regular" &&
    css`
      overflow-wrap: normal;
      word-break: normal;
    `}
  ${(props) =>
    props.$wordBreak === "smart" &&
    css`
      overflow-wrap: break-word;
    `}
  ${(props) =>
    props.$wordBreak === "all" &&
    css`
      word-break: break-all;
    `}

  //weight
  ${(props) =>
    props.$weight === "regular" &&
    css`
      ${tw`tw-font-regular`}
    `}
  ${(props) =>
    props.$weight === "light" &&
    css`
      ${tw`tw-font-light`}
    `}
  ${(props) =>
    props.$weight === "medium" &&
    css`
      ${tw`tw-font-medium`}
    `}
  ${(props) =>
    props.$weight === "bold" &&
    css`
      ${tw`tw-font-bold`}
    `}
`;

StyledTypography.displayName = "StyledTypography";
